import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";

import TimerIcon from "@mui/icons-material/Timer";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import dayjs from "dayjs";

import { useSendWhatsAppMessage } from "../../../messaging/hooks/useSendWhatsAppMessage";
import { normalizeCpf, validateCpf } from "src-next/shared/utils/cpf";
import { useSessionContext } from "@/contexts/SessionContext";

const COOLDOWN_SECONDS = 30;

/**
 * Nome do template cadastrado no WhatsApp Business (ajuste via env se necessário).
 * Deve existir na conta Meta vinculada ao ambiente.
 */
const CRM_SETUP_TEST_TEMPLATE_NAME =
  import.meta.env.VITE_CRM_SETUP_TEST_FLUXZAP_TEMPLATE_NAME ?? "86236825564_teste1231322";

/**
 * Template de domínio mínimo para o fluxo de teste (IDs estáveis só para UI).
 */
const CRM_SETUP_TEST_TEMPLATE = {
  id: "crm-setup-hardcoded-test",
  name: CRM_SETUP_TEST_TEMPLATE_NAME,
  preview: "Mensagem de teste do CRM Clínico (setup).",
} as const;

export interface CrmSetupSendTestStepProps {
  organizationId?: string;
  practitionerId?: string;
}

const CrmSetupSendTestStep = memo(
  ({ organizationId, practitionerId }: CrmSetupSendTestStepProps) => {
    const { user } = useSessionContext();
    const [cooldownSeconds, setCooldownSeconds] = useState(0);

    const sendFlow = useSendWhatsAppMessage({ organizationId });

    const hasFluxzapRequester = useMemo(() => {
      return Boolean(practitionerId);
    }, [organizationId, practitionerId]);

    const isCooldownActive = cooldownSeconds > 0;

    /**
     * Importante:
     * - O teste de envio deve usar o usuário logado como referência.
     * - `user.username` é tratado como CPF (quando aplicável) e validado antes do envio.
     */
    const cpfFromUser = useMemo(() => {
      return (user?.username ?? "").trim();
    }, [user?.username]);

    const cpfValidation = useMemo(
      () => validateCpf(cpfFromUser),
      [cpfFromUser],
    );
    const cpfDigits = useMemo(() => normalizeCpf(cpfFromUser), [cpfFromUser]);

    const canSend = useMemo(() => {
      if (isCooldownActive) return false;

      if (!cpfValidation.isValid) return false;
      if (sendFlow.isSending) return false;
      return true;
    }, [cpfValidation.isValid, isCooldownActive, sendFlow.isSending]);

    const cooldownLabel = useMemo(() => {
      if (!isCooldownActive) return null;

      const minutes = Math.floor(cooldownSeconds / 60);
      const seconds = cooldownSeconds % 60;
      const minutesLabel = String(minutes).padStart(2, "0");
      const secondsLabel = String(seconds).padStart(2, "0");
      return `${minutesLabel}:${secondsLabel}`;
    }, [cooldownSeconds, isCooldownActive]);

    const cooldownProgressValue = useMemo(() => {
      if (!isCooldownActive) return 0;
      const elapsedSeconds = COOLDOWN_SECONDS - cooldownSeconds;
      return Math.min(
        100,
        Math.round((elapsedSeconds / COOLDOWN_SECONDS) * 100),
      );
    }, [cooldownSeconds, isCooldownActive]);

    useEffect(() => {
      if (!isCooldownActive) return;

      const intervalId = window.setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) return 0;
          return prev - 1;
        });
      }, 1000);

      return () => {
        window.clearInterval(intervalId);
      };
    }, [isCooldownActive]);

    const handleSendTest = useCallback(async () => {
      if (!canSend) return;

      // Garante validação explícita antes do envio (mesmo se `canSend` estiver defasado).
      const validation = validateCpf(cpfFromUser);
      if (!validation.isValid) {
        toast.error(
          "Não foi possível enviar: seu usuário não possui um CPF válido em `user.username`.",
        );
        return;
      }

      const resolvedCpfDigits = normalizeCpf(cpfFromUser);

      const result = await sendFlow.send({
        patient: {
          id: `cpf-${resolvedCpfDigits}`,
          fullName: "Contato de teste (CRM setup)",
          cpf: resolvedCpfDigits,
        },
        appointment: {
          id: "crm-setup-send-test",
          startAtIso: dayjs().toISOString(),
        },
        template: {
          id: CRM_SETUP_TEST_TEMPLATE.id,
          name: CRM_SETUP_TEST_TEMPLATE.name,
          preview: CRM_SETUP_TEST_TEMPLATE.preview,
        },
        requesterReference: `PractitionerRole/${organizationId}-${practitionerId}`,
      });

      if (result.status === "success") {
        toast.success("Mensagem de teste enviada com sucesso.");
        setCooldownSeconds(COOLDOWN_SECONDS);
        return;
      }

      toast.error(result.errorMessage);
    }, [canSend, cpfFromUser, organizationId, practitionerId, sendFlow]);

    return (
      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: 2,
          p: { xs: 2, sm: 2.5 },
          bgcolor: "background.paper",
        }}
      >
        <Stack spacing={2}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "success100.main",
                border: "1px solid",
                borderColor: "success200.main",
              }}
            >
              <WhatsAppIcon sx={{ fontSize: 22, color: "success600.main" }} />
            </Box>

            <Box>
              <Typography variant="h6" fontWeight={700}>
                Teste de envio
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Valide o fluxo de envio antes de liberar as automações.
              </Typography>
            </Box>
          </Stack>

          {!cpfValidation.isValid && (
            <Alert severity="warning">
              Para enviar o teste, o campo{" "}
              <Typography component="span" variant="body2" fontWeight={700}>
                user.username
              </Typography>{" "}
              precisa conter um{" "}
              <Typography component="span" variant="body2" fontWeight={700}>
                CPF válido
              </Typography>
              . Valor atual:{" "}
              <Typography component="span" variant="body2" fontWeight={700}>
                {cpfFromUser || "—"}
              </Typography>
              .
            </Alert>
          )}

          {!hasFluxzapRequester && (
            <Alert severity="warning">
              Defina{" "}
              <Typography component="span" variant="body2" fontWeight={700}>
                VITE_FLUXZAP_REQUESTER_REFERENCE
              </Typography>{" "}
              (ex.: PractitionerRole/...) para habilitar o envio via FluxZap.
            </Alert>
          )}

          {sendFlow.hasError && sendFlow.errorMessage && (
            <Alert severity="error">{sendFlow.errorMessage}</Alert>
          )}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
              columnGap: { md: 2.5 },
              rowGap: 2,
              alignItems: "start",
            }}
          >
            <Box
              sx={{
                borderRadius: 2,
                bgcolor: "background.default",
                p: 1.5,
              }}
            >
              <Stack spacing={1.25}>
                <Typography variant="subtitle2" fontWeight={700}>
                  CPF (usuário logado)
                </Typography>

                <Box
                  sx={{
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    border: "1px solid",
                    borderColor: "divider",
                    px: 1.5,
                    py: 1.25,
                  }}
                >
                  <Typography variant="body2" fontWeight={800}>
                    {cpfDigits || "—"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Este valor vem de <strong>user.username</strong>. O envio só
                    é liberado quando for um CPF válido.
                  </Typography>
                </Box>

                <Typography variant="caption" color="text.secondary">
                  Template de teste:{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    fontWeight={700}
                  >
                    {CRM_SETUP_TEST_TEMPLATE.name}
                  </Typography>
                  . Ajuste com{" "}
                  <Typography
                    component="span"
                    variant="caption"
                    fontWeight={700}
                  >
                    VITE_CRM_SETUP_TEST_FLUXZAP_TEMPLATE_NAME
                  </Typography>{" "}
                  se o nome no WhatsApp for outro.
                </Typography>

                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1.25}
                  alignItems={{ xs: "flex-start", sm: "center" }}
                  justifyContent="space-between"
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1,
                      px: 1.25,
                      py: 0.75,
                      borderRadius: 2,
                      bgcolor: isCooldownActive
                        ? "primary100.main"
                        : "background.paper",
                    }}
                  >
                    <TimerIcon
                      sx={{
                        fontSize: 18,
                        color: isCooldownActive
                          ? "primary.main"
                          : "text.secondary",
                      }}
                    />
                    <Typography
                      variant="body2"
                      fontWeight={800}
                      sx={{
                        color: isCooldownActive
                          ? "primary.main"
                          : "text.primary",
                      }}
                    >
                      {isCooldownActive
                        ? `Aguarde ${cooldownLabel}`
                        : "Pronto para enviar"}
                    </Typography>
                  </Box>

                  <Button
                    variant="contained"
                    onClick={handleSendTest}
                    disabled={!canSend || !hasFluxzapRequester}
                    sx={{
                      textTransform: "none",
                      fontWeight: 800,
                      px: 2.25,
                      borderRadius: 2,
                    }}
                  >
                    {sendFlow.isSending ? "Enviando..." : "Enviar teste"}
                  </Button>
                </Stack>

                {isCooldownActive && (
                  <LinearProgress
                    variant="determinate"
                    value={cooldownProgressValue}
                    sx={{
                      borderRadius: 999,
                      height: 8,
                      backgroundColor: "background.paper",
                    }}
                  />
                )}
              </Stack>
            </Box>

            <Box
              sx={{
                border: "1px solid",
                borderColor: "divider",
                borderRadius: 2,
                bgcolor: "background.default",
                p: 1.5,
              }}
            >
              <Stack spacing={1}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 34,
                      height: 34,
                      borderRadius: 2,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "success100.main",
                      flexShrink: 0,
                    }}
                  >
                    <WhatsAppIcon
                      sx={{ fontSize: 18, color: "success600.main" }}
                    />
                  </Box>
                  <Typography variant="subtitle2" fontWeight={700}>
                    Pré-visualização
                  </Typography>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  CPF: {cpfDigits || "—"}
                </Typography>

                <Box
                  sx={{
                    borderRadius: 2,
                    bgcolor: "background.paper",
                    p: 1.25,
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      alignSelf: "flex-start",
                      maxWidth: "90%",
                      p: 1,
                      borderRadius: 2,
                      bgcolor: "neutral200.main",
                    }}
                  >
                    <Typography variant="caption" color="text.secondary">
                      FluxZap
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{ mt: 0.25, color: "text.primary" }}
                    >
                      {CRM_SETUP_TEST_TEMPLATE.preview}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      alignSelf: "flex-end",
                      maxWidth: "90%",
                      p: 1,
                      borderRadius: 2,
                      bgcolor: "success100.main",
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "success800.main" }}
                    >
                      Envio real via FluxZap após clicar em &quot;Enviar
                      teste&quot;.
                    </Typography>
                  </Box>
                </Box>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>
    );
  },
);

CrmSetupSendTestStep.displayName = "CrmSetupSendTestStep";

export default CrmSetupSendTestStep;
